import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn';

const BOXED =
  'w-full rounded-lg border border-line bg-sunk px-3.5 py-3 text-base text-ink outline-none ' +
  'placeholder:text-placeholder disabled:cursor-not-allowed disabled:opacity-40';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Renders the label above the control, as every publish-flow field does. */
  label?: ReactNode;
  /** Muted "(optional)" suffix beside the label. */
  optional?: boolean;
  hint?: ReactNode;
}

/** The boxed input used across the publish flow and the message composer. */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, optional = false, hint, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const control = <input ref={ref} id={inputId} className={cn(BOXED, className)} {...rest} />;

  if (!label) return control;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-label font-semibold text-ink-2">
        {label}
        {optional ? <span className="font-normal text-placeholder"> (optional)</span> : null}
      </label>
      {control}
      {hint ? <span className="text-meta text-subtle">{hint}</span> : null}
    </div>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  optional?: boolean;
  hint?: ReactNode;
}

/**
 * The boxed input's sibling for a closed set of choices — which vehicle, which
 * place. A native `<select>` rather than a drawn menu: the phone shells get the
 * platform picker for free, which is both the accessible answer and the one
 * that feels right on a device.
 *
 * `SelectChip` is a different control for a different job — a handful of
 * choices shown at once, all visible. This is for lists too long to lay out.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, optional = false, hint, className, id, children, ...rest },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const control = (
    <select
      ref={ref}
      id={selectId}
      // `appearance-none` would take the platform arrow with it and leave
      // nothing saying this opens; the native affordance is kept on purpose.
      className={cn(BOXED, 'cursor-pointer', className)}
      {...rest}
    >
      {children}
    </select>
  );

  if (!label) return control;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-label font-semibold text-ink-2">
        {label}
        {optional ? <span className="font-normal text-placeholder"> (optional)</span> : null}
      </label>
      {control}
      {hint ? <span className="text-meta text-subtle">{hint}</span> : null}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
}

/** The ride-description textarea: 12px radius, 150px floor, vertical resize only. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const areaId = id ?? generatedId;

  const control = (
    <textarea
      ref={ref}
      id={areaId}
      className={cn(
        BOXED,
        'min-h-[150px] resize-y rounded-2xl p-3.5 text-body-lg leading-relaxed',
        className,
      )}
      {...rest}
    />
  );

  if (!label) return control;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={areaId} className="text-label font-semibold text-ink-2">
        {label}
      </label>
      {control}
    </div>
  );
});

export interface BareFieldProps {
  /** Small uppercase caption above the value — "From", "To", "Date". */
  caption: string;
  children: ReactNode;
  className?: string;
}

/**
 * A borderless field inside the hero search widget. The widget itself carries
 * the border; each field is only a padded hover target.
 */
export function BareField({ caption, children, className }: BareFieldProps) {
  return (
    <label
      className={cn(
        'flex flex-1 cursor-text flex-col gap-0.5 rounded-2xl px-3.5 py-2.5',
        'transition-colors duration-fast ease-standard hover:bg-hover-soft',
        className,
      )}
    >
      <span className="text-tiny font-semibold tracking-eyebrow text-subtle uppercase">
        {caption}
      </span>
      {children}
    </label>
  );
}

/** The bare input that sits inside a {@link BareField}. */
export const BareInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function BareInput({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          'w-full border-none bg-transparent p-0 text-base font-semibold text-ink outline-none',
          'placeholder:text-placeholder placeholder:font-semibold',
          className,
        )}
        {...rest}
      />
    );
  },
);
